import {
  CopilotMessage,
  CopilotMessageStatus,
  useCopilotSessionStore,
} from '@/experiences/sales-portal/domain/copilot/screens/copilot-chat/blocs/stores/use-copilot-session-store';
import { useCallback, useMemo } from 'react';
import { useSalesPortalCopilotSendMessageMutation } from '@/experiences/sales-portal/domain/copilot/screens/copilot-chat/blocs/documents/sales-portal-copilot-send-message.generated';
import { v4 as uuidv4 } from 'uuid';

export interface SendCopilotMessageMutator {
  /**
   * Re-send a previously failed message.
   *
   * @param messageId - `id` of the failed message to retry.
   */
  retry: (messageId: string) => Promise<void>;
  /**
   * Send the user's message to Copilot.
   *
   * @param text - The message text to send.
   */
  send: (text: string) => Promise<void>;
}

interface UseSendCopilotMessageMutatorParams {
  onMessagesChange?: () => void;
}

const hasInFlightMessage = (messages: CopilotMessage[]): boolean =>
  messages.some((m) => m.status === CopilotMessageStatus.Sending);

/**
 * Hook exposing `send` and `retry` actions for the Copilot chat. Owns the
 * lifecycle for setting/updating messages in the {@link useCopilotSessionStore}.
 *
 * @param params.onMessagesChange - Called whenever the message list is
 *   appended to or a message changes status.
 *
 * @returns A {@link SendCopilotMessageMutator} exposing `send` and `retry` actions.
 */
export const useSendCopilotMessageMutator = ({
  onMessagesChange,
}: UseSendCopilotMessageMutatorParams = {}): SendCopilotMessageMutator => {
  const [sendMutation] = useSalesPortalCopilotSendMessageMutation();

  const runSend = useCallback(
    async (text: string, userMessageId: string) => {
      const { setSessionId, updateMessage } = useCopilotSessionStore.getState();

      // Keep track of the generation number, to compare against new messages coming in.
      // If the generations don't match up at the time of message, it was for an old session.
      const generation = useCopilotSessionStore.getState().generation;
      const isStaleSession = () =>
        useCopilotSessionStore.getState().generation !== generation;

      try {
        const sessionId = useCopilotSessionStore.getState().sessionId;
        const { data, errors } = await sendMutation({
          variables: { sessionId: sessionId ?? null, text },
        });

        if (isStaleSession()) {
          return;
        }

        const payload = data?.salesPortalCopilotSendMessage;
        if (errors?.length || !payload) {
          throw errors?.[0] ?? new Error('Empty copilot response');
        }

        if (payload.session_id) {
          setSessionId(payload.session_id);
        }
        updateMessage(userMessageId, { status: CopilotMessageStatus.Sent });
        useCopilotSessionStore.getState().appendMessages(
          payload.messages.map((message) => ({
            id: uuidv4(),
            role: 'copilot' as const,
            status: CopilotMessageStatus.Sent,
            text: message.text,
            timestamp: message.timestamp,
          })),
        );
      } catch {
        if (isStaleSession()) {
          return;
        }

        updateMessage(userMessageId, { status: CopilotMessageStatus.Failed });
      }
      onMessagesChange?.();
    },
    [onMessagesChange, sendMutation],
  );

  const send = useCallback(
    async (text: string) => {
      const { appendMessages, messages } = useCopilotSessionStore.getState();

      if (hasInFlightMessage(messages)) {
        return;
      }

      const userMessageId = uuidv4();
      appendMessages([
        {
          id: userMessageId,
          role: 'user',
          status: CopilotMessageStatus.Sending,
          text: text,
          timestamp: new Date().toISOString(),
        },
      ]);
      onMessagesChange?.();

      await runSend(text, userMessageId);
    },
    [onMessagesChange, runSend],
  );

  const retry = useCallback(
    async (messageId: string) => {
      const { messages, updateMessage } = useCopilotSessionStore.getState();
      const message = messages.find((m) => m.id === messageId);

      if (!message || hasInFlightMessage(messages)) {
        return;
      }

      updateMessage(messageId, { status: CopilotMessageStatus.Sending });
      await runSend(message.text, messageId);
    },
    [runSend],
  );

  return useMemo(() => ({ retry, send }), [retry, send]);
};
